import type { IsoTimeTz } from '../../types/iso-date';

/**
 * Validates ISO 8601 time with timezone.
 * Accepts formats like:
 * - THH:mm:ssZ
 * - THH:mm:ss(.fraction[1-6])?Z
 * - THH:mm:ss(+|-)HH:MM
 * - THH:mm:ss(.fraction[1-6])?(+|-)HH:MM
 *
 * Constraints:
 * - Hours: 00-23
 * - Minutes: 00-59
 * - Seconds: 00-59
 * - Fractional seconds: 1-6 digits allowed
 * - Timezone offset bounds: +14:00 (max), -12:00 (min). When absolute hour is at the limit, minutes must be 00.
 */
export function isIsoTimeTz(time: string): time is IsoTimeTz {
  const re =
    /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)(?:\.(\d{1,6}))?(?:Z|([+-])([01]\d|2[0-3]):([0-5]\d))$/;
  const m = re.exec(time);
  if (!m) return false;

  const sign = m[5]; // '+' | '-' | undefined (when 'Z')
  const tzHour = m[6] !== undefined ? Number(m[6]) : undefined;
  const tzMinute = m[7] !== undefined ? Number(m[7]) : undefined;

  if (sign) {
    if (tzHour === undefined || tzMinute === undefined) return false;
    if (sign === '+') {
      // +14:00 is maximum; when 14, minutes must be 00
      if (tzHour > 14) return false;
      if (tzHour === 14 && tzMinute !== 0) return false;
    } else {
      // -12:00 is minimum; when 12, minutes must be 00
      if (tzHour > 12) return false;
      if (tzHour === 12 && tzMinute !== 0) return false;
    }
  }

  return true;
}

